import { PropType, computed, defineComponent, onUnmounted, reactive, ref, watch } from 'vue'
import { Store } from 'pinia'
import { ChargeMapContext } from '../../chargerMap/index.model'
import { ComponentActions, ComponentState } from '../../../nsh5/localStores/component'
import { addMapResources, changeMarker, getDistanceByViewport } from '../../chargerMap/mapUtils'
import { useRoute, useRouter } from 'vue-router'
import { bd09toGcj02, gcj02ToBd09 } from '@/utils/coordinateTransform'
import { MapViewMode, Marker, Point, ZoomMode } from '../../../contract/map'
import { DEFAULT_DISTRICT_ZOOM_LEVEL } from '../../../config/zoomLevelConfig'
import debounce from 'lodash.debounce'
import { getPowerAroundV2 } from '@/pages/chargermap/apis/map.api'
import { ResourceItemModel } from '../../iconRender/icon.model'
/**
 * 县县通资源点渲染功能模块
 */
export type CountyLayoutProps = {
  onClickedMarker: (marker: Marker) => void
}
export default defineComponent({
  name: 'county-layout-resource-Icon-render-view',
  props: {
    mapContextRef: {
      type: Object as PropType<ChargeMapContext>,
    },
    mapInstance: {
      type: Object,
    },
    componentStore: {
      // 用于同一项目功能模块间的通信
      type: Object as PropType<Store<string, ComponentState, {}, ComponentActions>>,
      default: null,
    },
  },

  setup: (props, { expose }) => {
    const mapContextRef = computed(() => props.mapContextRef)
    const mapInstance = computed(() => props.mapInstance)
    const zoomLevel = ref<number>(0)
    let cacheClickedId = '' // 记录已经点击变大/已点击 的点
    const clickedMarkerMap: {
      [id: string]: Marker
    } = {} // 缓存点击过的点，后面可以直接通过ID获取

    const router = useRouter()
    const route = useRoute()

    const getFilterParams = () => {
      const itemCode =
        props.componentStore.countyLayoutConfig.countyLayoutConfigList[
          props.componentStore.countyLayoutConfig.currentIndex
        ].itemCode
      if (itemCode === 'swap_station') {
        return { 'recharge|ps': null }
      } else if (itemCode === 'charge_station') {
        return { 'recharge|cs': { resource_types: 'CS_DC,CS_AC' } }
      }
      return {}
    }

    let resources: ResourceItemModel[] = [] // 地图上画点的资源点
    let resResources: ResourceItemModel[] = [] // 通过接口请求到的资源点

    const clearMarks = () => {
      resources = []
      mapContextRef.value?.removeMarkers()
    }

    /**
     * 请求around接口获取资源
     * 地图删除ICON数据 = 已渲染在地图上的ICON数据  - （接口请求数据  与  已渲染在地图上的ICON数据）
     * 地图新画增量ICON数据  = （接口请求数据 或 已渲染在地图上的ICON数据 ）- 已渲染在地图上的ICON数据
     */
    const fetchMarkers = debounce(
      () => {
        const distance = getDistanceByViewport(mapContextRef.value!) // 获取视野范围的距离
        const pointGcj = bd09toGcj02(mapContextRef.value?.getCenter() as Point)
        zoomLevel.value = mapInstance.value?.getZoom()
        getPowerAroundV2(zoomLevel.value, getFilterParams(), pointGcj, distance).then((res) => {
          if (
            zoomLevel.value > DEFAULT_DISTRICT_ZOOM_LEVEL &&
            props.componentStore.viewMode == MapViewMode.CountyLayout
          ) {
            resResources = res.data.powers || []
            const resourcesIds = resources.map((item) => item.id) // 页面中已经存在的markers id
            const resTempMarkers = resResources.concat(resources) // 本次请求的markers + 保留点, 不需要额外判断filterChangedSaveMarkerId是否在resources中，后面的uniqueTempMarkers会去重
            const resTempMarkersIds = resTempMarkers.map((item) => item.id) // 本次请求的markers id
            const uniqueTempMarkers = resources
              .concat(resTempMarkers)
              .reduce((pre: ResourceItemModel[], cur: ResourceItemModel) => {
                if (!pre.find((item: ResourceItemModel) => item.id === cur.id)) {
                  pre.push(cur)
                }
                return pre
              }, []) // 去重后的markers
            const deleteMarkers = uniqueTempMarkers.filter((v) => !resTempMarkersIds.includes(v.id)) // 需要删除的markers
            const deleteMarkersIds = deleteMarkers.map((item) => item.id)
            const newMarkers = uniqueTempMarkers.filter((v) => !resourcesIds.includes(v.id)) // 需要新增的markers
            deleteMarkersIds.length && mapContextRef.value?.removeMarkers(deleteMarkersIds)
            addMapResources(mapContextRef.value!, ZoomMode.City, newMarkers)
            resources = resTempMarkers
          } else {
            clearMarks()
          }
        })
      },
      500,
      {}
    )

    const drawCountyLayoutIcon = () => {
      fetchMarkers.cancel()
      if (zoomLevel.value > DEFAULT_DISTRICT_ZOOM_LEVEL) {
        // 县级别开始画点
        fetchMarkers()
      } else if (zoomLevel.value <= DEFAULT_DISTRICT_ZOOM_LEVEL) {
        props.componentStore.bus.emit('clearPositionMarker') // 定位点
        clearMarks()
      }
    }

    /**
     * 点击地图上的点
     * @param customMarkerData
     * @returns
     */
    const onClickedMarker = (customMarkerData: Marker) => {
      if (cacheClickedId == customMarkerData.id) {
        // 防止对同一个点多次点击
        return
      }
      changeMarker(mapContextRef.value!, customMarkerData.id, cacheClickedId || '')
      cacheClickedId = customMarkerData.id
      if (props.componentStore.viewMode === MapViewMode.CountyLayout) {
        clickedMarkerMap[customMarkerData.id] = customMarkerData
        const resource: any = customMarkerData?.data
        // props.componentStore.bus.emit('clickedMarker', customMarkerData) // 触发画圈
        if (resource) {
          const routeTarget = {
            name: 'resource',
            query: {
              id: resource.id,
              location: resource.location,
              pointType: resource.pointType,
              pointSubType: resource.pointSubType,
            },
            state: {
              backHistoryNum: window.backHistoryNum,
            },
          }
          if (route.name == 'countyLayout') {
            window.backHistoryNum++
            routeTarget.state.backHistoryNum = window.backHistoryNum
            router.push(routeTarget)
          } else {
            router.replace(routeTarget)
          }
          const [lng, lat] = resource.location?.split(',') || [0, 0]
          const point = gcj02ToBd09({
            lng: Number(lng),
            lat: Number(lat),
          })
          // 设置目的地，显示小气泡
          props.componentStore.setDestLocationByPin({
            name: resource.name || 'Oops！未获取到名称',
            lng: point.lng,
            lat: point.lat,
            address: resource.address,
          })
          props.componentStore.$patch({
            cardLoaded: true,
          })
        }
      }
    }

    props.componentStore.bus.on('countyLayoutClickedMarker', onClickedMarker)

    // ------------暴露出去的方法--------------
    const zoomEnd = async () => {
      zoomLevel.value = mapInstance.value?.getZoom()
      // console.log('====================================')
      // console.log(
      //   'county-layout,,,zoomLevel.value: ',
      //   zoomLevel.value,
      //   props.componentStore.viewMode
      // )
      // console.log('====================================')
      drawCountyLayoutIcon()
    }

    const dragEnd = async () => {
      drawCountyLayoutIcon()
    }

    // 启动相关事件监听
    const initEventListener = () => {
      mapInstance.value?.addEventListener('zoomend', zoomEnd, false)
      mapInstance.value?.addEventListener('dragend', dragEnd, false)
    }

    onUnmounted(() => {
      console.log('==============onUnmounted======================')
      console.log()
      console.log('====================================')
      props.componentStore.bus.off('countyLayoutClickedMarker')
      mapInstance.value?.removeEventListener('zoomend', zoomEnd, false)
      mapInstance.value?.removeEventListener('dragend', dragEnd, false)
    })

    /**
     * 组件获取地图实例后的回调
     */
    const onReady = async () => {
      initEventListener()
    }
    watch(
      () => props.mapContextRef,
      () => {
        if (props.mapContextRef) {
          onReady()
        }
      },
      {
        immediate: true,
      }
    )

    watch(
      () => props.componentStore.countyLayoutConfig.currentIndex,
      () => {
        clearMarks() // 切换需要清除marker
        drawCountyLayoutIcon()
      },
      { immediate: true }
    )

    watch(
      () => route.name,
      (newVal, oldVal) => {
        // 资源点退回到县县通
        if (newVal === 'countyLayout' && oldVal === 'resource') {
          props.componentStore.unsetDestLocationByPin() // 小气泡消失
          if (zoomLevel.value > DEFAULT_DISTRICT_ZOOM_LEVEL) {
            changeMarker(mapContextRef.value!, '', cacheClickedId || '') // 大icon变小icon
          } else if (zoomLevel.value <= DEFAULT_DISTRICT_ZOOM_LEVEL) {
            props.componentStore.bus.emit('clearPositionMarker') // 定位点
            clearMarks()
          }
          cacheClickedId = ''
        }
      }
    )

    expose({
      onClickedMarker,
    })

    return () => {}
  },
})
